import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";
import { referralLetterOptionsSchema } from "./referral-template";
import { visitSourceIdsSchema } from "./visit-source";

const id = publicIdSchema;
// Patient summary reading level (Ontario RFS). Optional: older clients omit it
// and get "standard". Kept in step with @rosc/processing READING_LEVELS.
// Shown as Simple (grade 4-5), Standard (grade 6, the default) and High
// school level (grade 10-12); the value "detailed" stays for compatibility.
export const READING_LEVELS = ["simple", "standard", "detailed"] as const;
export const readingLevelSchema = z.enum(READING_LEVELS);
export type ReadingLevel = z.output<typeof readingLevelSchema>;
export const READING_LEVEL_DEFAULT: ReadingLevel = "standard";
// Patient summary output language (clinical lead, 04/11): the summary is
// written in this language whatever the note language. Optional: older
// clients omit it and keep today's behaviour (the account's note language,
// English by default). Chinese is split by script so Simplified and
// Traditional never mix. "pt" is Portuguese; which variant (Brazilian by
// default) is set in @rosc/processing PORTUGUESE_SUMMARY_VARIANT.
export const SUMMARY_LANGUAGES = [
  "en",
  "zh-Hans",
  "zh-Hant",
  "pt",
  "fr",
  "ja",
  "es",
] as const;
export const summaryLanguageSchema = z.enum(SUMMARY_LANGUAGES);
export type SummaryLanguage = z.output<typeof summaryLanguageSchema>;
export const SUMMARY_LANGUAGE_DEFAULT: SummaryLanguage = "en";
export const noteGenerationSchema = z
  .strictObject({
    template: z.enum([
      "SOAP Template",
      "Specialist Template",
      "Voice to Text Dictation",
      "Referral Letter",
      "Patient Summary",
      "Custom",
    ]),
    style: z.enum(["", "Paragraph", "Bullet Point"]),
    format: z.enum(["", "Detailed", "Concise"]),
    exam: z.enum(["", "Physical", "Virtual"]),
    customTemplatePublicId: id.nullable().default(null),
    macroPublicIds: z
      .array(id)
      .max(50)
      .refine((ids) => new Set(ids).size === ids.length, "Duplicate macro")
      .transform((ids) => ids.toSorted())
      .default([]),
    // Visit sources to include, kept apart from today's transcript.
    sourceIds: visitSourceIdsSchema.optional(),
    readingLevel: readingLevelSchema.optional(),
    summaryLanguage: summaryLanguageSchema.optional(),
    // Referral letter dialog (specialty, recipient, instructions (reason) and
    // letter template). Missing: the original referral letter.
    referral: referralLetterOptionsSchema.optional(),
  })
  .refine(
    (body) =>
      body.referral === undefined || body.template === "Referral Letter",
    "Referral details apply to referral letters only",
  )
  .refine(
    (body) =>
      body.readingLevel === undefined || body.template === "Patient Summary",
    "Reading level applies to patient summaries only",
  )
  .refine(
    (body) =>
      body.summaryLanguage === undefined || body.template === "Patient Summary",
    "Summary language applies to patient summaries only",
  )
  .refine(
    (body) =>
      body.template === "Custom"
        ? body.customTemplatePublicId !== null
        : body.customTemplatePublicId === null,
    "Custom template selection is inconsistent",
  );
export type NoteGenerationBody = z.output<typeof noteGenerationSchema>;
export const noteGenerationContract = {
  createVisitNote: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit or note source not found" },
      CONFLICT: { message: "Note generation conflict" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Note generation unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({
          public_id: publicIdSchema,
        }),
        query: z.strictObject({}).default({}),
        headers: z.object({ "idempotency-key": z.uuid().toLowerCase() }),
        body: noteGenerationSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/visits/{public_id}/notes",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(z.object({ public_id: id })),
};
